import { useEffect } from 'react';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image';
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const DEFAULT_SEO = {
  siteName: 'BasedOn',
  defaultTitle: 'Professional Power BI Visuals & Productivity Tools | BasedOn',
  defaultDescription: 'Professional Power BI custom visuals including Timeline Slicer and Countdown Timer. Space-saving, fast, and reliable solutions designed for data professionals.',
  defaultImage: 'https://based-on.net/og-image.png',
  siteUrl: 'https://based-on.net',
  twitterHandle: '@basedon_net',
};

export function SEOHead({
  title,
  description = DEFAULT_SEO.defaultDescription,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_SEO.defaultImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  canonical,
  noindex = false,
  nofollow = false,
  author,
  publishedTime,
  modifiedTime,
}: SEOProps) {
  
  const fullTitle = title 
    ? `${title} | ${DEFAULT_SEO.siteName}` 
    : DEFAULT_SEO.defaultTitle;
  
  const finalOgTitle = ogTitle || title || DEFAULT_SEO.defaultTitle;
  const finalOgDescription = ogDescription || description;
  const finalCanonical = canonical || window.location.href.split('?')[0].split('#')[0];

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (author) updateMetaTag('author', author);
    
    // Robots meta
    const robotsContent: string[] = [];
    if (noindex) robotsContent.push('noindex');
    if (nofollow) robotsContent.push('nofollow');
    if (robotsContent.length > 0) {
      updateMetaTag('robots', robotsContent.join(', '));
    }

    // Open Graph
    updateMetaTag('og:title', finalOgTitle, true);
    updateMetaTag('og:description', finalOgDescription, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', finalCanonical, true);
    updateMetaTag('og:site_name', DEFAULT_SEO.siteName, true);

    // Article-specific OG tags
    if (ogType === 'article') {
      if (publishedTime) updateMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) updateMetaTag('article:modified_time', modifiedTime, true);
      if (author) updateMetaTag('article:author', author, true);
    }

    // Twitter Card
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', finalOgTitle);
    updateMetaTag('twitter:description', finalOgDescription);
    updateMetaTag('twitter:image', ogImage);
    if (DEFAULT_SEO.twitterHandle) {
      updateMetaTag('twitter:site', DEFAULT_SEO.twitterHandle);
    }

    // Canonical URL
    updateLinkTag('canonical', finalCanonical);

  }, [
    fullTitle,
    description,
    keywords,
    finalOgTitle,
    finalOgDescription,
    ogImage,
    ogType,
    twitterCard,
    finalCanonical,
    noindex,
    nofollow,
    author,
    publishedTime,
    modifiedTime,
  ]);

  return null;
}

